import { isValidDecimalNumber, noZeroAfterDecimal } from 'utils/isValidDecimalNumber'
import { minMaxValidation } from 'utils/minBestMaxValidation'

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value)

const hasSupportedPrecision = (value) => {
  const decimalPart = value.substr(value.indexOf('.'))
  return decimalPart.length <= '127'
}

export const isNumericInputAllowed = (value) => {
  return isFiniteNumber(Number(value)) && hasSupportedPrecision(value)
}

// Altitude is optional, so a missing bound cannot be out of range. When both are given the
// lowest must not exceed the highest (equal is allowed).
export const isAltitudeRangeValid = (lowestAltitude, highestAltitude) => {
  const isMissing = (value) => value === null || value === undefined || value === ''

  if (isMissing(lowestAltitude) || isMissing(highestAltitude)) {
    return true
  }

  return Number(lowestAltitude) <= Number(highestAltitude)
}

export const toSavedNumber = (value) => {
  return value === '' ? null : Number(Number.parseFloat(value).toFixed(10))
}

export const handleRangeFieldChange = async ({
  value,
  firstValue,
  secondValue,
  validationFields,
  validationBoundary,
  setValue,
  setError,
  saveSiteData,
  saveFieldName,
  requireNoZeroOnValidRange = true,
  saveWithTimeout
}) => {
  const errorMessage = minMaxValidation(firstValue, secondValue, validationFields, validationBoundary)

  if (errorMessage.field === '') {
    if (isValidDecimalNumber(Number(value))) {
      if (!requireNoZeroOnValidRange || noZeroAfterDecimal(value)) {
        setValue(value)
        setError(errorMessage)
      }

      const payload = { [saveFieldName]: toSavedNumber(value) }

      if (saveWithTimeout) {
        await saveWithTimeout(() => saveSiteData(payload))
      } else {
        await saveSiteData(payload)
      }

      return { saved: true }
    }
    return { saved: false }
  }

  if (isValidDecimalNumber(Number(value)) && noZeroAfterDecimal(value)) {
    setValue(value)
    setError(errorMessage)
  }

  return { saved: false }
}

export const handleCalculatedCoverageChange = async ({
  value,
  setValue,
  saveSiteData,
  saveFieldName,
  saveWithTimeout
}) => {
  if (value === '') {
    setValue('')
    await saveWithTimeout(() =>
      saveSiteData({ [saveFieldName]: null })
    )
    return
  }

  if (!isNumericInputAllowed(value)) {
    return
  }

  if (noZeroAfterDecimal(value)) {
    setValue(value)
  }

  await saveWithTimeout(() =>
    saveSiteData({ [saveFieldName]: Number(value).toFixed(10) })
  )
}

export const hasPositiveCoverageValue = (value) => {
  const trimmed = String(value ?? '').trim()
  if (trimmed === '') {
    return false
  }

  const numericValue = Number(trimmed)
  return Number.isFinite(numericValue) && numericValue > 0
}

export const hasAlternativeCoverageValue = (value) => {
  return hasPositiveCoverageValue(value)
}

export const hasProtectedOrConservedCoverage = ({
  coveredByPa,
  coveredByOECM,
  paUserUpdated,
  oecmUserUpdated
}) => {
  return [
    coveredByPa,
    coveredByOECM,
    paUserUpdated,
    oecmUserUpdated
  ].some(hasAlternativeCoverageValue)
}
